import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Notice, Plate, TextField } from "@wilco/ui";
import { useState } from "react";
import { useCan } from "../auth.ts";
import { formatDate, formatNumber, t, type MessageKey } from "../i18n.ts";
import { ScanBar } from "../scan/ScanBar.tsx";
import { serviceApi, type Card } from "../service/api.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";

const route = getRouteApi("/app/production/unit");

const STEP: Record<string, MessageKey> = {
  TODO: "step.TODO",
  PROGRESS: "step.PROGRESS",
  PAUSED: "step.PAUSED",
  DONE: "step.DONE",
  SKIPPED: "step.SKIPPED",
  REJECTED: "step.REJECTED",
  CANCELLED: "step.CANCELLED",
};

const REPAIR: Record<string, MessageKey> = {
  CONFIRMED: "repair.state.CONFIRMED",
  UNDER_REPAIR: "repair.state.UNDER_REPAIR",
  DONE: "repair.state.DONE",
  CANCELLED: "repair.state.CANCELLED",
};

const PART: Record<string, MessageKey> = { ADD: "repair.part.ADD", REMOVE: "repair.part.REMOVE", SCRAP: "repair.part.SCRAP" };

/** Состояние строкой сервера: известное словами, новое как есть. */
const label = (map: Record<string, MessageKey>, state: string) => {
  const key = map[state];
  return key ? t(key) : state;
};

export const cardQuery = (code: string) => ({ queryKey: ["service", "card", code], queryFn: () => serviceApi.card(code), retry: false });

/**
 * «Виріб» по серийнику (ПС-15, SRV-08): модель и комплектация, где лежит,
 * маршрут с попытками, прошивки с историей, ремонты с деталями и работами.
 * Цен на экране нет. Серийник находится и в украинской раскладке (ПС-16).
 * «Сервіс» заводит отсюда ремонт изделия с «Карантину», мастер снимает
 * карантин (SRV-04).
 */
export function UnitPage() {
  const { serial } = route.useSearch();
  const navigate = useNavigate();
  const card = useQuery({ ...cardQuery(serial ?? ""), enabled: Boolean(serial) });
  const c = card.data;

  return (
    <div className="page stock-narrow">
      <h1 className="page-title">{t("unit.title")}</h1>
      <ScanBar onScan={(code) => void navigate({ to: "/production/unit", search: { serial: code }, replace: true })} placeholder={t("unit.scan")} autoFocus={!serial} />
      {card.error ? <Notice tone="danger" title={errorNotice(card.error).title} className="stock-notice" /> : null}
      {!serial ? <p className="w-muted stock-hint">{t("unit.empty")}</p> : null}
      {c ? <CardView key={c.serial} c={c} /> : null}
    </div>
  );
}

function CardView({ c }: { c: Card }) {
  const can = useCan();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [reason, setReason] = useState("");
  const openRepair = c.repairs.find((r) => r.state === "CONFIRMED" || r.state === "UNDER_REPAIR");
  const create = useMutation({ mutationFn: () => serviceApi.open(c.serial, reason.trim() || null) });
  const release = useMutation({ mutationFn: () => serviceApi.release(c.serial) });
  const attributes = Object.entries(c.attributes);

  return (
    <>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      <Plate cut="md" bordered className="shelf-card w-build-in">
        <div className="shelf-card-head">
          <span className="shelf-code w-num">{c.serial}</span>
          {c.assembledOn ? <span className="w-muted">{t("unit.made", { date: formatDate(c.assembledOn) })}</span> : null}
        </div>
        <div className="shelf-product">{c.model}</div>
        {attributes.length > 0 ? (
          <dl className="unit-attrs">
            {attributes.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        <div className="tags">
          <Badge>{c.family}</Badge>
          {c.quarantineReason ? <Badge tone="danger">{t("pos.quarantine", { reason: c.quarantineReason })}</Badge> : null}
          {c.packedWithOpenRoute ? <Badge tone="warning">{t("pos.openRoute")}</Badge> : null}
          {c.repairedWithOpenWorks ? <Badge tone="warning">{t("unit.openWorks")}</Badge> : null}
        </div>
      </Plate>

      <section className="stock-section" aria-labelledby="unit-where">
        <h2 id="unit-where" className="desk-group-title">
          {t("unit.where")}
        </h2>
        <p>{c.location ?? "—"}</p>
        {c.position ? <p className="w-muted">{t("pos.step", { step: c.position })}</p> : null}
      </section>

      {can("service.repair") && !openRepair && c.quarantineReason ? (
        <section className="stock-section unit-actions" aria-label={t("repair.open")}>
          <TextField label={t("repair.reason")} value={reason} onChange={(e) => setReason(e.target.value)} autoComplete="off" />
          <Button
            variant="primary"
            disabled={create.isPending}
            onClick={() =>
              create.mutate(undefined, {
                onSuccess: (r) => void navigate({ to: "/service/repairs/$id", params: { id: r.id } }),
                onError: (e) => setNotice(errorNotice(e)),
              })
            }
          >
            {t("repair.open")}
          </Button>
        </section>
      ) : null}
      {can("route.master") && (Boolean(c.quarantineReason) || c.repairedWithOpenWorks) ? (
        <div className="order-actions">
          <Button
            variant="ghost"
            disabled={release.isPending}
            onClick={() =>
              release.mutate(undefined, {
                onSuccess: () => {
                  void queryClient.invalidateQueries({ queryKey: ["service"] });
                  setNotice({ tone: "success", title: t("unit.released", { serial: c.serial }) });
                },
                onError: (e) => setNotice(errorNotice(e)),
              })
            }
          >
            {t("unit.release")}
          </Button>
        </div>
      ) : null}

      <section className="stock-section" aria-labelledby="unit-route">
        <h2 id="unit-route" className="desk-group-title">
          {t("unit.route")}
        </h2>
        {c.rejections > 0 ? <p className="w-muted">{t("unit.rejects", { count: c.rejections })}</p> : null}
        {c.steps.length === 0 ? <p className="w-muted">{t("unit.noSteps")}</p> : null}
        <ol className="steps">
          {c.steps.map((s, i) => (
            <li key={`${s.name}-${String(s.attempt)}-${String(i)}`} className="step" data-closed={s.state === "DONE" ? "" : undefined}>
              <span className="step-name">
                {s.name}
                {s.attempt > 1 ? ` #${String(s.attempt)}` : ""}
                {s.note ? <span className="w-muted step-skill">{s.note}</span> : null}
              </span>
              <span className="step-state" data-state={s.state}>
                {label(STEP, s.state)}
              </span>
              <span className="w-muted step-who">{s.employee ?? ""}</span>
              <span className="w-num step-time">{s.minutes > 0 ? t("unit.minutes", { minutes: formatNumber(s.minutes) }) : ""}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="stock-section" aria-labelledby="unit-firmware">
        <h2 id="unit-firmware" className="desk-group-title">
          {t("unit.firmware")}
        </h2>
        {c.firmware.length === 0 ? <p className="w-muted">{t("unit.noFirmware")}</p> : null}
        <ul className="payments">
          {c.firmware.map((f, i) => (
            <li key={`${f.version}-${String(i)}`} className="payment">
              <span className="w-num">{formatDate(f.at.slice(0, 10))}</span>
              <span className="payment-ref">
                {f.employee}
                {f.repairId ? <span className="w-muted">{` · ${t("unit.repair")}`}</span> : null}
              </span>
              <span className="w-num payment-sum">{f.version}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="stock-section" aria-labelledby="unit-repairs">
        <h2 id="unit-repairs" className="desk-group-title">
          {t("unit.repairs")}
        </h2>
        {c.repairs.length === 0 ? <p className="w-muted">{t("unit.noRepairs")}</p> : null}
        {c.repairs.map((r) => (
          <Plate key={r.number} cut={false} className="repair">
            <div className="queue-card-head">
              <span className="w-num queue-doc">{r.number}</span>
              <span className="w-muted">{[formatDate(r.createdOn), label(REPAIR, r.state)].join(" · ")}</span>
            </div>
            {r.reason ? <p className="w-muted">{r.reason}</p> : null}
            <ul className="repair-works">
              {r.works.map((w, i) => (
                <li key={`w-${String(i)}`}>{w.name}</li>
              ))}
              {r.parts.map((p, i) => (
                <li key={`p-${String(i)}`}>
                  {`${label(PART, p.action)}: ${p.name} × ${formatNumber(p.qty)}`}
                  {p.partSerial ? <span className="w-num w-muted">{` ${p.partSerial}`}</span> : null}
                </li>
              ))}
            </ul>
            {r.openWorksAtClose.length > 0 ? <p className="line-card-alert">{t("unit.unfinished", { works: r.openWorksAtClose.join(", ") })}</p> : null}
          </Plate>
        ))}
      </section>
    </>
  );
}
